import { useState } from "react";
import { useNavigate, Link, Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Eye, EyeOff, ArrowLeft } from "lucide-react";

const LOGO = "https://customer-assets.emergentagent.com/job_0890dc80-6146-4c2d-b11f-fa0b2de6d70b/artifacts/ekzdqal8_image.png";

function getRedirectPath(role) {
  if (role === "administrator" || role === "finance" || role === "store_manager" || role === "kepala_cabang") return "/admin";
  if (role === "kasir") return "/pos";
  return "/portal";
}

export default function LoginPage() {
  const [isRegister, setIsRegister] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const { user, login, register } = useAuth();
  const navigate = useNavigate();

  // Redirect if already logged in
  if (user) return <Navigate to={getRedirectPath(user.role)} replace />;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      let data;
      if (isRegister) {
        data = await register(email, password, name, phone);
        toast.success("Registrasi berhasil!");
      } else {
        data = await login(email, password);
        toast.success("Login berhasil!");
      }
      if (data.role === "administrator" || data.role === "finance" || data.role === "store_manager" || data.role === "kepala_cabang") {
        navigate("/admin");
      } else if (data.role === "kasir") {
        navigate("/pos");
      } else {
        navigate("/portal");
      }
    } catch (err) {
      const msg = formatApiError(err.response?.data?.detail) || err.message;
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center px-6">
      <div className="w-full max-w-md">
        <div className="mb-8 text-center">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-[#E60000] transition-colors mb-8" data-testid="back-to-home">
            <ArrowLeft size={16} /> Kembali ke Beranda
          </Link>
          <img src={LOGO} alt="FI Barbershop" className="w-16 h-16 mx-auto mb-4 rounded-full border border-[#E60000]/30" />
          <h1 className="font-heading text-3xl font-bold uppercase tracking-tight text-white">
            {isRegister ? "Daftar" : "Masuk"}
          </h1>
          <p className="text-gray-400 text-sm mt-2">
            {isRegister ? "Buat akun baru di FI Barbershop" : "Masuk ke akun FI Barbershop Anda"}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="bg-[#0F0F0F] border border-[#262626] p-8 space-y-5" data-testid="auth-form">
          {error && <div className="bg-red-900/20 border border-red-800 text-red-400 text-sm p-3" data-testid="auth-error">{error}</div>}

          {isRegister && (
            <>
              <div>
                <Label className="text-gray-300 text-sm font-semibold uppercase tracking-wider">Nama Lengkap</Label>
                <Input data-testid="register-name-input" className="mt-1.5 bg-[#1A1A1A] border-[#262626] text-white h-11 rounded-none focus:border-[#E60000]" value={name} onChange={e => setName(e.target.value)} placeholder="Nama lengkap" required />
              </div>
              <div>
                <Label className="text-gray-300 text-sm font-semibold uppercase tracking-wider">No. Telepon</Label>
                <Input data-testid="register-phone-input" className="mt-1.5 bg-[#1A1A1A] border-[#262626] text-white h-11 rounded-none focus:border-[#E60000]" value={phone} onChange={e => setPhone(e.target.value)} placeholder="08xxxxxxxxxx" />
              </div>
            </>
          )}

          <div>
            <Label className="text-gray-300 text-sm font-semibold uppercase tracking-wider">Email</Label>
            <Input data-testid="login-email-input" type="email" className="mt-1.5 bg-[#1A1A1A] border-[#262626] text-white h-11 rounded-none focus:border-[#E60000]" value={email} onChange={e => setEmail(e.target.value)} placeholder="email@contoh.com" required />
          </div>

          <div>
            <Label className="text-gray-300 text-sm font-semibold uppercase tracking-wider">Password</Label>
            <div className="relative mt-1.5">
              <Input data-testid="login-password-input" type={showPw ? "text" : "password"} className="bg-[#1A1A1A] border-[#262626] text-white h-11 rounded-none focus:border-[#E60000] pr-10" value={password} onChange={e => setPassword(e.target.value)} placeholder="Password" required />
              <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white" data-testid="toggle-password">
                {showPw ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          <Button type="submit" disabled={loading} className="w-full bg-[#E60000] hover:bg-[#FF1A1A] text-white font-bold uppercase tracking-wider h-11 rounded-none" data-testid="auth-submit-btn">
            {loading ? "Memproses..." : isRegister ? "Daftar" : "Masuk"}
          </Button>

          <p className="text-center text-sm text-gray-400">
            {isRegister ? "Sudah punya akun?" : "Belum punya akun?"}{" "}
            <button type="button" onClick={() => { setIsRegister(!isRegister); setError(""); }} className="text-[#E60000] hover:text-[#FF1A1A] font-semibold" data-testid="toggle-auth-mode">
              {isRegister ? "Masuk" : "Daftar"}
            </button>
          </p>
        </form>
      </div>
    </div>
  );
}
